import { Component, OnInit, AfterViewInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Observable, forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import { SampleHistory } from 'src/app/models/sample.model';
import { SampleService } from 'src/app/services/sample.service';
import { ToastService } from 'src/app/services/toast.service';
import { CommonService } from 'src/app/services/common.service';
import { ValueConversionService } from 'src/app/services/value-conversion.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { AuthService } from 'src/app/services/auth.service';
import { GridViewService } from 'src/app/services/grid-view.service';
import { GridSettingsService } from 'src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service';
import { HistoryGridComponent } from 'src/app/shared/grid/history-grid/history-grid.component';
import { GridModel } from 'src/app/shared/grid/grid.model';
import { ColumnConfig } from 'src/app/shared/grid/core-grid/columnConfig.model';
import { ColumnDetails } from 'src/app/models/columnDetails.model';
import { sampleDetailsRouteConfig } from 'src/app/shared/constants/sampleDetailsRouteConfig.constants';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { GridPermissions, gridToPermissionFlags } from 'src/app/shared/grid/core-grid/gridPermissions.enum';
import { GridIds } from 'src/app/shared/constants/gridIds.constants';
import { SampleStatusEnum } from 'src/app/enums/sampleStatus.enum';
import { SampleTypeEnum } from 'src/app/enums/sampleType.enum';
import { SampleCategoryEnum } from 'src/app/enums/sampleCategory.enum';
import { UnsampledTimeCorrectionType } from 'src/app/enums/unsampledTimeCorrectionType.enum';
import { ExposureTypicality } from 'src/app/enums/exposureTypicality.enum';
import { SampleShiftCoverage } from 'src/app/enums/sampleShiftType.enum';
import { WeatherSourceType } from 'src/app/enums/WeatherSourceType.enum';
import { WeatherConditionType } from 'src/app/enums/weatherConditionType.enum';
import { ShiftType } from 'src/app/enums/shiftTypeValue.enum';
import { getNamedEnum } from 'src/app/_helpers/enum-helper';
import { DateFormatPipe } from 'src/app/shared/pipes/date-format.pipe';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { Lookup } from 'src/app/models/lookup.model';
import { SampleSurveyService } from 'src/app/services/sample-survey.service';
import { SegService } from 'src/app/services/seg.service';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { SamplingEquipmentService } from 'src/app/services/sampling-equipment.service';
import { SampleMediaService } from 'src/app/services/sample-media.service';
import { CalibrationEquipmentService } from 'src/app/services/calibration-equipment.service';
import { UserService } from 'src/app/services/user.service';
import { LabRequisitionService } from 'src/app/services/lab-requisition.service';
import { SensorAssignmentService } from 'src/app/services/sensorAssignment.service';
import { DeviceCalibrationHistoryService } from 'src/app/services/deviceCalibrationHistory.service';
import { TranslateService } from 'src/app/services/translate.service';
import { ParameterTypeEnum } from 'src/app/enums/parameterType.enum';

@Component({
  standalone: false,
  selector: 'app-sample-history',
  templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
  styleUrls: ['./../../shared/grid/core-grid/core-grid.component.scss', './sample-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DateFormatPipe]
})
export class SampleHistoryComponent extends HistoryGridComponent<SampleHistory, string> implements OnInit, AfterViewInit, OnDestroy {
  sampleId: string;
  dateFormat: string;
  sampleStatusEnum = getNamedEnum(SampleStatusEnum);

  sampleSurveys: Lookup[] = [];
  users: Lookup[] = [];
  organizationLevels: Lookup[] = [];
  samplingEquipment: Lookup[] = [];
  sampleMedia: Lookup[] = [];
  calibrationEquipment: Lookup[] = [];
  labRequisitions: Lookup[] = [];
  segs: Lookup[] = [];
  sensorAssignments: Lookup[] = [];
  deviceCalibrationHistories: Lookup[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    protected sampleService: SampleService,
    protected toastService: ToastService,
    protected dialog: MatDialog,
    protected commonService: CommonService,
    protected valueConversionService: ValueConversionService,
    protected dateFormatService: DateFormatService,
    private activatedRoute: ActivatedRoute,
    private dateFormatPipe: DateFormatPipe,
    private sampleSurveyService: SampleSurveyService,
    private segService: SegService,
    private organizationLevelService: OrganizationLevelService,
    private samplingEquipmentService: SamplingEquipmentService,
    private sampleMediaService: SampleMediaService,
    private calibrationEquipmentService: CalibrationEquipmentService,
    private userService: UserService,
    private labRequisitionService: LabRequisitionService,
    private sensorAssignmentService: SensorAssignmentService,
    private deviceCalibrationHistoryService: DeviceCalibrationHistoryService,
    authService: AuthService,
    gridViewService: GridViewService,
    gridSettingsService: GridSettingsService,
    private translateService: TranslateService
  ) {
    super(toastService, dialog, commonService, authService, gridViewService, gridSettingsService);

    this.sampleId = this.activatedRoute.snapshot.params.id;
    this.setEntityId(this.sampleId);
    this.tabs = this.initializeTabs();
  }

  protected override buildHistoryParams(): HttpParams {
    const baseParams = super.buildHistoryParams();
    return baseParams.set('sampleId', this.entityId.toString());
  }

  protected getHistoryRecords(params: HttpParams): Observable<GridModel<SampleHistory>> {
    return this.sampleService.getHistory(params);
  }

  protected getHistoryFieldConfiguration(): Observable<{ [property: string]: ColumnDetails }> {
    return this.sampleService.getHistoryFieldConfiguration();
  }

  protected override get objectId(): string {
    return GridIds.SampleHistory;
  }

  protected override get isGridViewEnabled(): boolean {
    return true;
  }

  // The role permissions carry no object of their own for a sample's history, so the grid reads its access from
  // the sample: anyone who can read the sample can view, scroll and page its history. Only the read bits pass
  // through, since the history is a read-only view.
  protected override applyRolePermission(): void {
    const samplePermission = this.permissionService.getRolePermission(RolePermissionObjects.Sample);
    this.enforceRolePermissions(samplePermission
      ? { ...samplePermission, permissions: samplePermission.permissions & gridToPermissionFlags[GridPermissions.Read] }
      : null);
  }

  get hasOrgFilter() {
    return true;
  }

  ngOnInit(): void {
    this.dateFormat = this.dateFormatService.userDateWithTimeFormat;
    super.ngOnInit();

    forkJoin({
      sampleSurveys: this.sampleSurveyService.getAll(),
      users: this.userService.getAll(),
      organizationLevels: this.organizationLevelService.getAll(),
      samplingEquipment: this.samplingEquipmentService.getAll(),
      sampleMedia: this.sampleMediaService.getAll(),
      calibrationEquipment: this.calibrationEquipmentService.getAll(),
      labRequisitions: this.labRequisitionService.getAll(),
      segs: this.segService.getAll(),
      sensorAssignments: this.sensorAssignmentService.getAll(),
      deviceCalibrationHistories: this.deviceCalibrationHistoryService.getAll()
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: results => {
        this.sampleSurveys = results.sampleSurveys;
        this.users = results.users;
        this.organizationLevels = results.organizationLevels;
        this.samplingEquipment = results.samplingEquipment;
        this.sampleMedia = results.sampleMedia;
        this.calibrationEquipment = results.calibrationEquipment;
        this.labRequisitions = results.labRequisitions;
        this.segs = results.segs;
        this.sensorAssignments = results.sensorAssignments;
        this.deviceCalibrationHistories = results.deviceCalibrationHistories;
      },
      error: () => this.toastService.displayErrorMessage('Failed to load lookup data', true)
    });
  }

  override ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    super.ngOnDestroy();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.loadHeaderData();
  }

  override initializeProperties(): void {
    this.addEntityTitle = 'Sample History';
    this.sortActive = nameOf<SampleHistory>(x => x.dateUpdated);
    this.defaultOrderBy = 'desc';

    this.addDisabled = true;
    this.editDisabled = true;
    this.deleteDisabled = true;
  }

  override initializeColumns(): void {
    this.additionalActions = [];
    this.columnConfigs = [
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.startDate),
        displayName: 'Date and Time',
        cellTemplate: (history) => history.startDate ? this.dateFormatService.convertUTCDateStringToUserTimeZoneString(history.startDate.toString(), this.dateFormatService.userDateWithTimeFormat) : '-',
        cssClass: (history) => this.isFieldChanged(history, 'StartDate') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.endDate),
        displayName: 'End Date',
        cellTemplate: (history) => history.endDate ? this.dateFormatPipe.transform(history.endDate) : '-',
        cssClass: (history) => this.isFieldChanged(history, 'EndDate') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.sampleNumber),
        displayName: 'Sample Number',
        cellTemplate: (history) => history.sampleNumber || '-',
        cssClass: (history) => this.isFieldChanged(history, 'SampleNumber') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.alternateSampleNumber),
        displayName: 'Alternate Sample Number',
        cellTemplate: (history) => history.alternateSampleNumber || '-',
        cssClass: (history) => this.isFieldChanged(history, 'AlternateSampleNumber') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.sampleSurveyId),
        displayName: 'Survey',
        cellTemplate: (history) => {
          const survey = this.sampleSurveys.find(s => s.id === history.sampleSurveyId);
          return survey ? survey.name : '-';
        },
        cssClass: (history) => this.isFieldChanged(history, 'SampleSurveyId') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.segId),
        displayName: 'SEG',
        cellTemplate: (history) => {
          const seg = this.segs.find(s => s.id === history.segId);
          return seg ? seg.name : '-';
        },
        cssClass: (history) => this.isFieldChanged(history, 'SegId') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.sampleStatus),
        displayName: 'Sample Status',
        cellTemplate: (history) => this.getSampleStatusDisplay(history),
        enumFilterMap: this.enumToValueNameArray(SampleStatusEnum),
        cssClass: (history) => this.isFieldChanged(history, 'SampleStatus') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.sampleCategory),
        displayName: 'Sample Category',
        cellTemplate: (history) => history.sampleCategory != null ? this.translateService.getDynamicTranslation(this.commonService.getEnumName(SampleCategoryEnum, history.sampleCategory)) : '-',
        enumFilterMap: this.enumToValueNameArray(SampleCategoryEnum),
        cssClass: (history) => this.isFieldChanged(history, 'SampleCategory') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.isFinished),
        displayName: 'Finished',
        cellTemplate: (history) => this.translateService.getDynamicTranslation(history.isFinished ? 'Yes' : 'No'),
        cssClass: (history) => this.isFieldChanged(history, 'IsFinished') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.sampleType),
        displayName: 'Sample Type',
        cellTemplate: (history) => history.sampleType != null ? this.translateService.getDynamicTranslation(this.commonService.getEnumName(SampleTypeEnum, history.sampleType)) : '-',
        enumFilterMap: this.enumToValueNameArray(SampleTypeEnum),
        cssClass: (history) => this.isFieldChanged(history, 'SampleType') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.sensorAssignmentId),
        displayName: 'Sensor Assignment',
        cellTemplate: (history) => {
          const sensorAssignment = this.sensorAssignments.find(s => s.id === history.sensorAssignmentId);
          return sensorAssignment ? sensorAssignment.name : '-';
        },
        cssClass: (history) => this.isFieldChanged(history, 'SensorAssignmentId') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.createdByUserId),
        displayName: 'Created By',
        cellTemplate: (history) => history.createdByUserName || '-',
        cssClass: (history) => this.isFieldChanged(history, 'CreatedByUserName') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.dateCreated),
        displayName: 'Date Created',
        cellTemplate: (history) => history.dateCreated ? this.dateFormatPipe.transform(history.dateCreated) : '-',
        cssClass: (history) => this.isFieldChanged(history, 'DateCreated') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.unsampledTimeCorrection),
        displayName: 'Unsampled Time Correction',
        cellTemplate: (history) => history.unsampledTimeCorrection != null ? this.translateService.getDynamicTranslation(this.commonService.getEnumName(UnsampledTimeCorrectionType, history.unsampledTimeCorrection)) : '-',
        enumFilterMap: this.enumToValueNameArray(UnsampledTimeCorrectionType),
        cssClass: (history) => this.isFieldChanged(history, 'UnsampledTimeCorrection') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.samplePreCal),
        displayName: 'Sample Pre Cal',
        cellTemplate: (history) => this.withUnit(history.samplePreCal, this.valueConversionService.getParameterUnit(ParameterTypeEnum.FlowRate)),
        cssClass: (history) => this.isFieldChanged(history, 'SamplePreCal') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.samplePostCal),
        displayName: 'Sample Post Cal',
        cellTemplate: (history) => this.withUnit(history.samplePostCal, this.valueConversionService.getParameterUnit(ParameterTypeEnum.FlowRate)),
        cssClass: (history) => this.isFieldChanged(history, 'SamplePostCal') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.sampleAverageCal),
        displayName: 'Sample Average Cal',
        cellTemplate: (history) => this.withUnit(history.sampleAverageCal, this.valueConversionService.getParameterUnit(ParameterTypeEnum.FlowRate)),
        cssClass: (history) => this.isFieldChanged(history, 'SampleAverageCal') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.shiftLength),
        displayName: 'Shift Length',
        cellTemplate: (history) => {
          const shiftLength = history?.shiftLength;
          if (shiftLength === null || shiftLength === undefined || Number.isNaN(shiftLength)) return 'N/A';
          const minutes = this.valueConversionService.convertSecondsToMinutes(shiftLength);
          if (minutes < 0) return 'Invalid';
          return `${minutes} min(s)`;
        },
        cssClass: (history) => this.isFieldChanged(history, 'ShiftLength') ? 'highlight' : '',
        valueOutputTransformRule: (value: number) => value * 60,
        valueInputTransformRule: (value: number) => value / 60
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.exposureTypicality),
        displayName: 'Exposure Typicality',
        cellTemplate: (history) => history.exposureTypicality != null ? this.translateService.getDynamicTranslation(this.commonService.getEnumName(ExposureTypicality, history.exposureTypicality)) : '-',
        enumFilterMap: this.enumToValueNameArray(ExposureTypicality),
        cssClass: (history) => this.isFieldChanged(history, 'ExposureTypicality') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.updatedByUserId),
        displayName: 'Updated By',
        cellTemplate: (history) => history.updatedByUserName || '-',
        cssClass: (history) => this.isFieldChanged(history, 'UpdatedByUserId') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.dateUpdated),
        displayName: 'Date Updated',
        cellTemplate: (history) => history.dateUpdated ? this.dateFormatPipe.transform(history.dateUpdated) : '-',
        cssClass: (history) => this.isFieldChanged(history, 'DateUpdated') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.organizationLevelId),
        displayName: 'Visibility',
        cellTemplate: (history) => {
          const orgLevel = this.organizationLevels.find(o => o.id === history.organizationLevelId);
          return orgLevel ? orgLevel.name : '-';
        },
        cssClass: (history) => this.isFieldChanged(history, 'OrganizationLevelId') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.samplingEquipmentId),
        displayName: 'Sampling Equipment',
        cellTemplate: (history) => {
          const equipment = this.samplingEquipment.find(e => e.id === history.samplingEquipmentId);
          return equipment ? equipment.name : '-';
        },
        cssClass: (history) => this.isFieldChanged(history, 'SamplingEquipmentId') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.sampleMediaId),
        displayName: 'Sample Media',
        cellTemplate: (history) => {
          const media = this.sampleMedia.find(m => m.id === history.sampleMediaId);
          return media ? media.name : '-';
        },
        cssClass: (history) => this.isFieldChanged(history, 'SampleMediaId') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.calibrationEquipmentId),
        displayName: 'Calibration Equipment',
        cellTemplate: (history) => {
          const equipment = this.calibrationEquipment.find(e => e.id === history.calibrationEquipmentId);
          return equipment ? equipment.name : '-';
        },
        cssClass: (history) => this.isFieldChanged(history, 'CalibrationEquipmentId') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.blankSampleNumber),
        displayName: 'Blank Sample Number',
        cellTemplate: (history) => history.blankSampleNumber || '-',
        cssClass: (history) => this.isFieldChanged(history, 'BlankSampleNumber') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.sampledByUsername),
        displayName: 'Sampled By Username',
        cellTemplate: (history) => history.sampledByUsername || '-',
        cssClass: (history) => this.isFieldChanged(history, 'SampledByUsername') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.managingHygienistUserName),
        displayName: 'Managing Hygienist Username',
        cellTemplate: (history) => history.managingHygienistUserName || '-',
        cssClass: (history) => this.isFieldChanged(history, 'ManagingHygienistUserName') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.voidReason),
        displayName: 'Void Reason',
        cellTemplate: (history) => history.voidReason || '-',
        cssClass: (history) => this.isFieldChanged(history, 'VoidReason') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.shiftType),
        displayName: 'Shift Type',
        cellTemplate: (history) => history.shiftType != null ? this.translateService.getDynamicTranslation(this.commonService.getEnumName(SampleShiftCoverage, history.shiftType)) : '-',
        enumFilterMap: this.enumToValueNameArray(SampleShiftCoverage),
        cssClass: (history) => this.isFieldChanged(history, 'ShiftType') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.summaryNote),
        displayName: 'Summary Note',
        cellTemplate: (history) => history.summaryNote || '-',
        cssClass: (history) => this.isFieldChanged(history, 'SummaryNote') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.weatherSource),
        displayName: 'Weather Source',
        cellTemplate: (history) => history.weatherSource != null ? this.translateService.getDynamicTranslation(this.commonService.getEnumName(WeatherSourceType, history.weatherSource)) : '-',
        enumFilterMap: this.enumToValueNameArray(WeatherSourceType),
        cssClass: (history) => this.isFieldChanged(history, 'WeatherSource') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.temperatureMinC),
        displayName: 'Temperature Min',
        cellTemplate: (history) => this.withUnit(history.temperatureMinC, this.valueConversionService.getParameterUnit(ParameterTypeEnum.Temperature)),
        cssClass: (history) => this.isFieldChanged(history, 'TemperatureMinC') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.temperatureMaxC),
        displayName: 'Temperature Max',
        cellTemplate: (history) => this.withUnit(history.temperatureMaxC, this.valueConversionService.getParameterUnit(ParameterTypeEnum.Temperature)),
        cssClass: (history) => this.isFieldChanged(history, 'TemperatureMaxC') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.humidityPercent),
        displayName: 'Humidity (%)',
        cellTemplate: (history) => history.humidityPercent?.toString() || '-',
        cssClass: (history) => this.isFieldChanged(history, 'HumidityPercent') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.pressureMmhg),
        displayName: 'Pressure (mmHg)',
        cellTemplate: (history) => history.pressureMmhg?.toString() || '-',
        cssClass: (history) => this.isFieldChanged(history, 'PressureMmhg') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.weatherCondition),
        displayName: 'Weather Condition',
        cellTemplate: (history) => history.weatherCondition != null ? this.commonService.getEnumName(WeatherConditionType, history.weatherCondition) : '-',
        enumFilterMap: this.enumToValueNameArray(WeatherConditionType),
        cssClass: (history) => this.isFieldChanged(history, 'WeatherCondition') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.deviceCalibrationHistoryId),
        displayName: 'Device Calibration History',
        cellTemplate: (history) => {
          const deviceCalibration = this.deviceCalibrationHistories.find(d => d.id === history.deviceCalibrationHistoryId);
          return deviceCalibration ? deviceCalibration.name : '-';
        },
        cssClass: (history) => this.isFieldChanged(history, 'DeviceCalibrationHistoryId') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.sampleVolume),
        displayName: 'Sample Volume',
        cellTemplate: (history) => this.withUnit(history.sampleVolume, this.valueConversionService.getVolumeUnit()),
        cssClass: (history) => this.isFieldChanged(history, 'SampleVolume') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.correctedVolume),
        displayName: 'Corrected Volume',
        cellTemplate: (history) => this.withUnit(history.correctedVolume, this.valueConversionService.getVolumeUnit()),
        cssClass: (history) => this.isFieldChanged(history, 'CorrectedVolume') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.outlierReason),
        displayName: 'Outlier Reason',
        cellTemplate: (history) => history.outlierReason || '-',
        cssClass: (history) => this.isFieldChanged(history, 'OutlierReason') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.locationComments),
        displayName: 'Location Comments',
        cellTemplate: (history) => history.locationComments || '-',
        cssClass: (history) => this.isFieldChanged(history, 'LocationComments') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.labRequisitionId),
        displayName: 'Lab Requisition',
        cellTemplate: (history) => {
          const labReq = this.labRequisitions.find(l => l.id === history.labRequisitionId);
          return labReq ? labReq.name : '-';
        },
        cssClass: (history) => this.isFieldChanged(history, 'LabRequisitionId') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.deletedByUserId),
        displayName: 'Deleted By',
        cellTemplate: (history) => this.users?.find(u => u.id.toString() === history.deletedByUserId)?.name || '-',
        cssClass: (history) => this.isFieldChanged(history, 'DeletedByUserId') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.deletedDate),
        displayName: 'Deleted Date',
        cellTemplate: (history) => history.deletedDate ? this.dateFormatPipe.transform(history.deletedDate) : '-',
        cssClass: (history) => this.isFieldChanged(history, 'DeletedDate') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.isDeleted),
        displayName: 'Is Deleted',
        cellTemplate: (history) => history.isDeleted ? 'Yes' : 'No',
        cssClass: (history) => this.isFieldChanged(history, 'IsDeleted') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.runTime),
        displayName: 'Run Time (min)',
        cellTemplate: (history) => {
          const runTime = history?.runTime;
          if (runTime === null || runTime === undefined || Number.isNaN(runTime)) return 'N/A';
          const minutes = this.valueConversionService.convertSecondsToMinutes(runTime);
          return `${minutes} min(s)`;
        },
        cssClass: (history) => this.isFieldChanged(history, 'RunTime') ? 'highlight' : '',
        valueOutputTransformRule: (value: number) => value * 60,
        valueInputTransformRule: (value: number) => value / 60
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.prePostCalVariabilityFlag),
        displayName: 'Pre Post Cal Variability Flag',
        cellTemplate: (history) => history.prePostCalVariabilityFlag ? 'Yes' : 'No',
        cssClass: (history) => this.isFieldChanged(history, 'PrePostCalVariabilityFlag') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.calibrationOverdueFlag),
        displayName: 'Calibration Overdue Flag',
        cellTemplate: (history) => history.calibrationOverdueFlag ? 'Yes' : 'No',
        cssClass: (history) => this.isFieldChanged(history, 'CalibrationOverdueFlag') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.mediaLotNumber),
        displayName: 'Media Lot Number',
        cellTemplate: (history) => history.mediaLotNumber || '-',
        cssClass: (history) => this.isFieldChanged(history, 'MediaLotNumber') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.shiftRoster),
        displayName: 'Shift Roster',
        cellTemplate: (history) => history.shiftRoster != null ? this.commonService.getEnumName(ShiftType, history.shiftRoster).replace(/_/g, ' ') : '-',
        enumFilterMap: this.enumToValueNameArray(ShiftType),
        cssClass: (history) => this.isFieldChanged(history, 'ShiftRoster') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.approvedByUserName),
        displayName: 'Approved By',
        cellTemplate: (history) => history.approvedByUserName || '-',
        cssClass: (history) => this.isFieldChanged(history, 'ApprovedByUserId') ? 'highlight' : ''
      }),
      new ColumnConfig<SampleHistory>({
        name: nameOf<SampleHistory>(x => x.approvedDate),
        displayName: 'Approved On',
        cellTemplate: (history) => history.approvedDate ? this.dateFormatPipe.transform(history.approvedDate) : '-',
        cssClass: (history) => this.isFieldChanged(history, 'ApprovedDate') ? 'highlight' : ''
      })
    ];
    this.setColumnConfig();
  }

  private setColumnConfig(): void {
    if (this.columnDetails) {
      for (const [key, config] of Object.entries(this.columnDetails)) {

        const columnConfig = this.columnConfigs.find(c => c.name === key);
        if (columnConfig) {
          columnConfig.displayName = config.displayName || columnConfig.displayName;
        }
      }
    }
  }

  override objectFilterMap(propertyName: string): any | undefined {
    switch (propertyName) {
      case nameOf<SampleHistory>(x => x.sampleSurveyId):
        return this.sampleSurveyService;
      case nameOf<SampleHistory>(x => x.segId):
        return this.segService;
      case nameOf<SampleHistory>(x => x.organizationLevelId):
        return this.organizationLevelService;
      case nameOf<SampleHistory>(x => x.samplingEquipmentId):
        return this.samplingEquipmentService;
      case nameOf<SampleHistory>(x => x.sampleMediaId):
        return this.sampleMediaService;
      case nameOf<SampleHistory>(x => x.calibrationEquipmentId):
        return this.calibrationEquipmentService;
      case nameOf<SampleHistory>(x => x.labRequisitionId):
        return this.labRequisitionService;
      case nameOf<SampleHistory>(x => x.sensorAssignmentId):
        return this.sensorAssignmentService;
      case nameOf<SampleHistory>(x => x.deviceCalibrationHistoryId):
        return this.deviceCalibrationHistoryService;
      case nameOf<SampleHistory>(x => x.updatedByUserId):
        return this.userService;
      case nameOf<SampleHistory>(x => x.deletedByUserId):
        return this.userService;
      case nameOf<SampleHistory>(x => x.createdByUserId):
        return this.userService;
      default:
        return undefined;
    }
  }

  private isFieldChanged(history: SampleHistory, fieldName: string): boolean {
    if (!history.changedFields) {
      return false;
    }

    try {
      const fields = JSON.parse(history.changedFields);
      if (Array.isArray(fields)) {
        return fields.includes(fieldName);
      }
    } catch (e) {
    }

    return false;
  }

  override initializeTabs() {
    if (!this.sampleId) {
      return [];
    }
    return sampleDetailsRouteConfig(this.sampleId.toString());
  }

  private getSampleStatusDisplay(history: SampleHistory): string {
    if (history.sampleStatus == null) {
      return '-';
    }
    const translation = this.translateService.getDynamicTranslation(this.sampleStatusEnum[history.sampleStatus]);
    return translation || '-';
  }

  private withUnit(value: number | null | undefined, unit: string): string {
    return value == null ? '-' : `${value} ${unit}`.trim();
  }
}
